/**
 * Details tab for a pair.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  User,
  UserCog,
  Calendar,
  Edit2,
  AlertTriangle,
  type LucideIcon,
  XCircle,
  Users,
  Star,
  StarOff,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { type UserSituationConfig } from '@/components/pairs/pair-configs';
import type {
  PairActorRef,
  PairKpis,
  PairDetailRecord,
  PairHistoryEvent,
  PairManagedByRef,
  PairRoleRef,
  ParentInfo,
} from '@/types/remote/pair.types';
import {
  formatDate,
  formatDateTime,
  getAbsenceRateColor,
  getUserSituationConfig,
  formatPairAbsenceLabel,
  formatPairAverageScore,
  formatPairHistoryEventDetails,
  getDissociationOtherLabel,
  getDissociationReasonConfig,
  getPairHistoryEventDotClass,
  hasPairParentInfo,
  isOtherDissociationReason,
  isPairAverageScoreStarFilled,
  pairActorRoleChips,
} from '@/components/pairs/remote/pair-table-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { resolveLucideIcon } from '@/hooks/useRoleConfig';
import { getRoleByCode, getRoleColorCss, getRoleLabel } from '@/utils/role-label-utils';

/** Dissociate CTA — disabled with a tooltip when the pair is archived. */
export function PairDissociateAction({
  isArchived,
  onDissociate,
}: Readonly<{ isArchived: boolean; onDissociate?: () => void }>) {
  if (isArchived) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button variant="outline" size="sm" disabled icon={XCircle}>
                Dissocier
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{getErrorMessage('cannotUnlinkArchivedPair')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (!onDissociate) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      icon={XCircle}
      className="border-[rgb(var(--status-danger))] text-[rgb(var(--status-danger))] hover:bg-[color-mix(in_srgb,rgb(var(--status-danger))_10%,transparent)]"
      onClick={onDissociate}
    >
      Dissocier
    </Button>
  );
}

/** Tutor / student / manager identity card inside the details tab. */
function PairPersonCard({
  icon,
  title,
  colorName,
  name,
  profileHref,
  email,
  phone,
  situationCfg,
  lastLoginAt,
  dateInscription,
  finEngagement,
  parentInfo,
}: Readonly<{
  icon: LucideIcon;
  title: string;
  colorName: 'color-2' | 'color-3';
  name: string;
  profileHref: string;
  email?: string | null;
  phone?: string | null;
  situationCfg: UserSituationConfig | null;
  lastLoginAt?: string | null;
  dateInscription?: string | null;
  finEngagement?: string | null;
  parentInfo?: ParentInfo;
}>) {
  return (
    <DetailsCard icon={icon} title={title} colorName={colorName} colSpan={1}>
      <DetailField
        label="Nom"
        value={
          <Link to={profileHref} className="font-semibold hover:underline">
            {name}
          </Link>
        }
      />
      {email !== undefined && <DetailField label="Email" value={email ?? '—'} />}
      {phone !== undefined && <DetailField label="Téléphone" value={phone ?? '—'} />}
      {situationCfg && (
        <DetailField
          label="Situation"
          value={
            <LabelBadge
              label={situationCfg.label}
              icon={situationCfg.icon}
              variant={situationCfg.badgeVariant}
              color={situationCfg.badgeColor}
              size="sm"
            />
          }
        />
      )}
      <DetailField label="Dernière connexion" value={formatDateTime(lastLoginAt)} />
      {dateInscription !== undefined && (
        <DetailField label="Inscription" value={formatDate(dateInscription)} />
      )}
      {finEngagement !== undefined && (
        <DetailField label="Fin d'engagement" value={formatDate(finEngagement)} />
      )}

      {hasPairParentInfo(parentInfo) && (
        <div className="detail-section-highlighted mt-3">
          <div className="detail-section-header">
            <div className="detail-section-icon bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]">
              <Users className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
            </div>
            <span className="detail-section-title">Informations parent</span>
          </div>
          <div className="detail-section-content">
            <DetailField label="Email" value={parentInfo?.parentEmail ?? '—'} />
            <DetailField label="Téléphone" value={parentInfo?.parentPhone ?? '—'} />
          </div>
        </div>
      )}
    </DetailsCard>
  );
}

/** KPI strip: absence rate, average score, sessions, reports. */
function PairStatsCard({
  kpis,
  sessionsPlanned,
  reportsWritten,
  reportsTotal,
}: Readonly<{
  kpis: PairKpis | undefined;
  sessionsPlanned: number;
  reportsWritten: number;
  reportsTotal: number;
}>) {
  return (
    <DetailsCard icon={TrendingUp} title="Statistiques" colorName="color-1" colSpan="full">
      {!kpis && <EmptyPlaceholder message="Aucune statistique disponible" />}
      {kpis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DetailField
              vertical
              label="Séances"
              value={`${kpis.sessionsCompleted}/${sessionsPlanned}`}
            />
            <DetailField
              vertical
              label="Comptes rendus"
              value={`${reportsWritten}/${reportsTotal}`}
            />
            <DetailField
              vertical
              label="Absences élève"
              value={
                <span className={getAbsenceRateColor(kpis.studentAbsenceRate)}>
                  {formatPairAbsenceLabel(kpis.studentAbsences, kpis.studentAbsenceRate)}
                </span>
              }
            />
            <DetailField
              vertical
              label="Absences tuteur"
              value={
                <span className={getAbsenceRateColor(kpis.tutorAbsenceRate)}>
                  {formatPairAbsenceLabel(kpis.tutorAbsences, kpis.tutorAbsenceRate)}
                </span>
              }
            />
          </div>

          <div className="flex items-center gap-2 pt-3 mt-1 border-t border-border">
            <span className="text-sm font-medium text-muted-foreground">Note moyenne :</span>
            <span className="font-bold text-base text-foreground">
              {formatPairAverageScore(kpis.averageScore)}/5
            </span>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }, (_, i) =>
                isPairAverageScoreStarFilled(kpis.averageScore, i) ? (
                  <Star key={`star-${i}`} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                ) : (
                  <StarOff key={`star-${i}`} className="w-4 h-4 text-muted-foreground" />
                ),
              )}
            </div>
          </div>
        </>
      )}
    </DetailsCard>
  );
}

/** Vertical timeline of pair history events (create, swap, dissociate). */
export function PairHistoryTimeline({
  loading,
  events,
}: Readonly<{ loading: boolean; events: PairHistoryEvent[] | undefined }>) {
  if (loading) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm">
        Chargement de l'historique...
      </div>
    );
  }
  if (!events || events.length === 0) {
    return <EmptyPlaceholder message="Aucun historique disponible" />;
  }

  return (
    <div className="relative pl-5 border-l-2 border-muted space-y-4">
      {events.map((event) => {
        const detailsText = formatPairHistoryEventDetails(event);
        return (
          <div key={event.id} className="relative">
            <span
              className={cn(
                'absolute -left-[1.65rem] top-1 w-3 h-3 rounded-full ring-2 ring-background',
                getPairHistoryEventDotClass(event.eventType),
              )}
            />
            <div className="bg-muted/30 rounded-lg p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{event.label}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(event.timestamp)}
                </span>
              </div>
              {event.actor && (
                <div className="text-xs text-muted-foreground">
                  par <span className="font-medium">{event.actor.fullName}</span>
                </div>
              )}
              {detailsText && (
                <div className="text-xs text-muted-foreground mt-1">{detailsText}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Badge for a known reason; free-text label when the API reason is `other`. */
function DissociationReasonValue({
  isOtherReason,
  description,
  reasonCfg,
  fallbackReason,
}: Readonly<{
  isOtherReason: boolean;
  description?: string | null;
  reasonCfg: ReturnType<typeof getDissociationReasonConfig>;
  fallbackReason?: string;
}>) {
  if (isOtherReason) {
    return (
      <span className="text-sm text-foreground">{getDissociationOtherLabel(description)}</span>
    );
  }
  if (reasonCfg) {
    return (
      <LabelBadge
        label={reasonCfg.label}
        icon={reasonCfg.icon}
        variant={reasonCfg.badgeVariant}
        color={reasonCfg.badgeColor}
        size="sm"
      />
    );
  }
  return fallbackReason ?? '—';
}

/** One role chip for a pair actor. */
function renderPairActorRoleBadge(role: PairRoleRef): React.ReactNode {
  return (
    <LabelBadge
      key={role.code}
      label={getRoleLabel(role.code, role.label)}
      icon={resolveLucideIcon(getRoleByCode(role.code)?.icon)}
      variant="outlined"
      color={getRoleColorCss(role.code)}
      size="sm"
      className="!min-w-0"
    />
  );
}

/** Role chips next to a pair actor name. */
function renderPairActorRoleBadges(
  actor: PairActorRef | PairManagedByRef | null | undefined,
): React.ReactNode {
  const chips = pairActorRoleChips(actor);
  if (chips.length === 0) return null;
  return <span className="inline-flex flex-wrap gap-1">{chips.map(renderPairActorRoleBadge)}</span>;
}

/** Props for {@link PairDetailsTab}. */
interface PairDetailsTabProps {
  pair: PairDetailRecord;
  kpis: PairKpis | undefined;
  sessionsPlanned: number;
  reportsWritten: number;
  reportsTotal: number;
  isArchived: boolean;
  canEdit: boolean;
  onEdit?: (pairId: string) => void;
}

/** People, stats, and dissociation reason for the remote pair details tab. */
export const PairDetailsTab: React.FC<PairDetailsTabProps> = ({
  pair,
  kpis,
  sessionsPlanned,
  reportsWritten,
  reportsTotal,
  isArchived,
  canEdit,
  onEdit,
}) => {
  const tutorSituationCfg = getUserSituationConfig(pair.tutorSituation);
  const studentSituationCfg = getUserSituationConfig(pair.studentSituation);
  const dissociation = pair.dissociationDetails;
  const reasonCfg = getDissociationReasonConfig(dissociation?.reason);
  const isOtherReason = isOtherDissociationReason(dissociation?.reason);

  function handleEditManager(): void {
    onEdit?.(pair.id);
  }

  function renderManagedByValue(): React.ReactNode {
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        {pair.managedBy?.fullName ?? '—'}
        {renderPairActorRoleBadges(pair.managedBy)}
        {canEdit ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            tooltipText="Modifier le gestionnaire"
            icon={Edit2}
            onClick={handleEditManager}
          />
        ) : null}
      </span>
    );
  }

  function renderCreatedByValue(): React.ReactNode {
    if (!pair.createdBy?.fullName) return '—';
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        {pair.createdBy.fullName}
        {renderPairActorRoleBadges(pair.createdBy)}
      </span>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      <PairPersonCard
        icon={GraduationCap}
        title={getRoleLabel('tutor')}
        colorName="color-2"
        name={pair.tutorName}
        profileHref={`/tutors/${pair.tutorUserId}`}
        email={pair.tutorEmail ?? pair.tutorUser?.email}
        phone={pair.tutorDetail?.phone ?? pair.tutorPhone ?? pair.tutorUser?.phone}
        situationCfg={tutorSituationCfg}
        lastLoginAt={pair.tutorDetail?.lastLoginAt}
        dateInscription={pair.tutorDetail?.dateInscription}
        finEngagement={pair.tutorDetail?.finEngagement}
      />

      <PairPersonCard
        icon={User}
        title={getRoleLabel('student')}
        colorName="color-3"
        name={pair.studentName}
        profileHref={`/students/${pair.studentUserId}`}
        situationCfg={studentSituationCfg}
        lastLoginAt={pair.studentDetail?.lastLoginAt}
        parentInfo={pair.parentInfo}
      />

      <div className="md:col-span-2">
        <PairStatsCard
          kpis={kpis}
          sessionsPlanned={sessionsPlanned}
          reportsWritten={reportsWritten}
          reportsTotal={reportsTotal}
        />
      </div>

      <DetailsCard icon={UserCog} title="Gestion" colorName="color-2">
        <DetailField label="Géré par" value={renderManagedByValue()} />
        <DetailField label="Créé par" value={renderCreatedByValue()} />
      </DetailsCard>

      <DetailsCard icon={Calendar} title="Dates clés" colorName="color-3">
        <DetailField label="Date d'affectation" value={formatDateTime(pair.createdAt)} />
        <DetailField
          label="Dernière séance"
          value={kpis?.lastSessionDate ? formatDate(kpis.lastSessionDate) : 'Aucune séance'}
        />
        <DetailField
          label="Prochaine séance"
          value={kpis?.nextSessionDate ? formatDate(kpis.nextSessionDate) : 'Aucune séance prévue'}
        />
      </DetailsCard>

      {isArchived && dissociation && (
        <div className="md:col-span-2 rounded-2xl p-5 border border-[color-mix(in_srgb,rgb(var(--status-danger))_30%,transparent)] bg-[color-mix(in_srgb,rgb(var(--status-danger))_5%,transparent)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0 bg-[color-mix(in_srgb,rgb(var(--status-danger))_15%,transparent)]">
              <AlertTriangle className="w-5 h-5 text-[rgb(var(--status-danger))]" />
            </div>
            <h4 className="text-base font-semibold text-[rgb(var(--status-danger))]">
              Dissociation
            </h4>
          </div>
          <div className="space-y-3">
            <DetailField
              label="Motif"
              value={
                <DissociationReasonValue
                  isOtherReason={isOtherReason}
                  description={dissociation.description}
                  reasonCfg={reasonCfg}
                  fallbackReason={dissociation.reason}
                />
              }
            />
            {!isOtherReason && dissociation.description && (
              <DetailField label="Commentaire" value={dissociation.description} />
            )}
            <DetailField label="Dissocié le" value={formatDate(dissociation.date)} />
            {pair.lastUpdatedBy && <DetailField label="Par" value={pair.lastUpdatedBy.fullName} />}
          </div>
        </div>
      )}
    </div>
  );
};
